// src/storage/sessionGuard.ts — Daily Practice Time Guard & Cognitive Health (FR-ADHD-003)
// Tracks cumulative daily practice time, enforces configurable daily time limits (default 30 min),
// and presents gentle Clawey rest guidance to prevent hyper-focus and ADHD burnout.

import { tokens } from '../ui/designTokens';

export const DAILY_LIMIT_KEY = 'clawey_daily_limit_minutes';
export const PRACTICE_TIME_KEY = 'clawey_daily_practice_seconds_v1';
export const DEFAULT_DAILY_LIMIT_MINUTES = 30;
export const ALLOWED_DAILY_LIMIT_OPTIONS = [15, 20, 25, 30, 45, 60];

export interface DailyPracticeRecord {
  [dateIso: string]: number; // date in YYYY-MM-DD -> seconds practiced
}

export class SessionGuard {
  /** Get today's ISO date string (YYYY-MM-DD) */
  static getTodayDateString(): string {
    return new Date().toISOString().split('T')[0] ?? '2026-10-16';
  }

  /** Retrieve configurable daily time limit in minutes */
  static getDailyLimitMinutes(): number {
    try {
      const stored = localStorage.getItem(DAILY_LIMIT_KEY);
      if (stored) {
        const val = parseInt(stored, 10);
        if (ALLOWED_DAILY_LIMIT_OPTIONS.includes(val)) {
          return val;
        }
      }
    } catch {
      // Ignore storage error
    }
    return DEFAULT_DAILY_LIMIT_MINUTES;
  }

  /** Update configurable daily time limit in minutes */
  static setDailyLimitMinutes(minutes: number): void {
    const validMinutes = ALLOWED_DAILY_LIMIT_OPTIONS.includes(minutes)
      ? minutes
      : DEFAULT_DAILY_LIMIT_MINUTES;
    try {
      localStorage.setItem(DAILY_LIMIT_KEY, String(validMinutes));
    } catch {
      // Ignore
    }
  }

  /** Load all stored practice records */
  private static loadRecords(): DailyPracticeRecord {
    try {
      const raw = localStorage.getItem(PRACTICE_TIME_KEY);
      if (raw) {
        return JSON.parse(raw) as DailyPracticeRecord;
      }
    } catch {
      // Ignore
    }
    return {};
  }

  /** Save practice records */
  private static saveRecords(records: DailyPracticeRecord): void {
    try {
      localStorage.setItem(PRACTICE_TIME_KEY, JSON.stringify(records));
    } catch {
      // Ignore
    }
  }

  /** Add practice seconds to today's tally */
  static recordPracticeTime(seconds: number): {
    cumulativeSeconds: number;
    cumulativeMinutes: number;
    limitMinutes: number;
    isLimitExceeded: boolean;
  } {
    const safeSecs = Math.max(0, Math.round(seconds));
    const today = this.getTodayDateString();
    const records = this.loadRecords();
    const current = records[today] ?? 0;
    const updated = current + safeSecs;
    records[today] = updated;
    this.saveRecords(records);

    const limitMinutes = this.getDailyLimitMinutes();
    const cumulativeMinutes = Math.floor(updated / 60);
    const isLimitExceeded = cumulativeMinutes >= limitMinutes;

    return {
      cumulativeSeconds: updated,
      cumulativeMinutes,
      limitMinutes,
      isLimitExceeded,
    };
  }

  /** Get total practice seconds completed today */
  static getTodayPracticeSeconds(): number {
    const records = this.loadRecords();
    return records[this.getTodayDateString()] ?? 0;
  }

  /** Get total practice minutes completed today */
  static getTodayPracticeMinutes(): number {
    return Math.floor(this.getTodayPracticeSeconds() / 60);
  }

  /** Checks if today's practice has met or exceeded the daily limit */
  static isDailyLimitReached(): boolean {
    const minutes = this.getTodayPracticeMinutes();
    return minutes >= this.getDailyLimitMinutes();
  }

  /** Reset today's practice time for testing or parental override */
  static resetTodayPracticeTime(): void {
    const today = this.getTodayDateString();
    const records = this.loadRecords();
    delete records[today];
    this.saveRecords(records);
  }

  /** Creates a gentle rest suggestion modal for Tudor */
  static createRestWarningModal(onContinue: () => void, onTakeBreak?: () => void): HTMLElement {
    const modalOverlay = document.createElement('div');
    modalOverlay.id = 'session-guard-modal';
    modalOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(10, 25, 47, 0.75);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      animation: fadeIn 0.3s ease;
    `;

    const limitMin = this.getDailyLimitMinutes();
    const todayMin = this.getTodayPracticeMinutes();

    modalOverlay.innerHTML = `
      <div class="morris-card morris-card-gold" style="max-width: 520px; width: 100%; padding: 36px 30px; text-align: center; box-shadow: 0 16px 48px rgba(7, 20, 36, 0.4);">
        <div style="font-size: 3.5rem; margin-bottom: 12px; filter: drop-shadow(0 4px 12px rgba(229, 169, 60, 0.3));">
          🌊
        </div>
        <div style="display: inline-block; padding: 4px 14px; border-radius: 20px; background: rgba(61, 90, 69, 0.15); color: ${tokens.color.seaweedSage}; font-weight: 800; font-size: 0.85rem; text-transform: uppercase; margin-bottom: 12px;">
          Expedition Target Achieved
        </div>
        <h2 style="font-size: 1.85rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 14px;">
          Splendid Work Today, Tudor!
        </h2>
        <p style="font-size: 1.1rem; line-height: 1.6; color: #3A4D66; margin: 0 0 24px;">
          You have completed <strong>${todayMin} minutes</strong> of learning today (target: ${limitMin} min). Clawey recommends resting your naturalist eyes to let your discoveries sink in!
        </p>
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button id="btn-guard-take-break" class="touch-target btn-primary" style="padding: 14px 24px; font-size: 1.1rem; width: 100%;">
            🧘 Take a Rest & Go Play
          </button>
          <button id="btn-guard-continue-anyway" class="touch-target" style="padding: 10px 20px; font-size: 0.95rem; background: transparent; border: 1.5px solid rgba(26, 54, 93, 0.3); border-radius: 12px; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
            I Feel Fresh, Continue a Little &rarr;
          </button>
        </div>
      </div>
    `;

    const breakBtn = modalOverlay.querySelector<HTMLButtonElement>('#btn-guard-take-break');
    breakBtn?.addEventListener('click', () => {
      modalOverlay.remove();
      if (onTakeBreak) {
        onTakeBreak();
      } else {
        window.location.hash = '#/';
      }
    });

    const continueBtn = modalOverlay.querySelector<HTMLButtonElement>('#btn-guard-continue-anyway');
    continueBtn?.addEventListener('click', () => {
      modalOverlay.remove();
      onContinue();
    });

    return modalOverlay;
  }
}
